import { PropsWithChildren, ReactNode } from 'react';
import { PhotoAndTextCardFieldsFragment } from '../../../@types/generated';
import clsx from 'clsx';
import { ImageWithFocalPoint } from '../ImageWithFocalPoint';
import { BREAKPOINTS } from '@/config/constants';
import dynamic from 'next/dynamic';
import VideoPlayer from '../VideoPlayer';
const VideoJSPlayer = dynamic(() => import('@/components/VideoJS/VideoPlayer'), { ssr: false });

type Props = {
  data: PhotoAndTextCardFieldsFragment;
};

const VideoJS = ({ data }: Props) => {
  return (
    data?.video?.__typename === 'VideoJs' && (
      <div className="grid w-full place-items-center overflow-hidden rounded-28">
        <VideoJSPlayer
          options={{ sources: [{ src: data.video.mediaFileUrl, type: 'application/x-mpegURL' }], ...data.video }}
        />
      </div>
    )
  );
};

const Video = ({ data }: Props) => {
  return (
    data?.video?.__typename === 'Video' && (
      <div className="grid h-full w-full overflow-hidden rounded-28">
        <VideoPlayer
          videoId={data.video.youtubeId}
          autoPlay={data.video.autoplay}
          muted={data.video.muted}
          loop={data.video.loop}
        />
      </div>
    )
  );
};

const MediaMobile = ({ data }: Props) => {
  const isVideo = data?.video?.__typename === 'Video' || data?.video?.__typename === 'VideoJs';

  if (!data.image && !isVideo) {
    return;
  }

  const MobileWrapper = (props: { children: ReactNode }) => (
    <div className={`relative col-span-5 mb-32 grid h-[272px] transition-all sm:h-[384px] md:hidden`}>
      {props.children}
    </div>
  );
  const MobileVideoJSWrapper = (props: { children: ReactNode }) => (
    <div className={`relative col-span-5 mb-32 transition-all md:hidden`}>{props.children}</div>
  );

  if (data?.video?.__typename === 'VideoJs') {
    return (
      <MobileVideoJSWrapper>
        <VideoJS data={data} />
      </MobileVideoJSWrapper>
    );
  }

  if (data?.video?.__typename === 'Video') {
    return (
      <MobileWrapper>
        <Video data={data} />
      </MobileWrapper>
    );
  }

  return (
    <MobileWrapper>
      {data?.image && (
        <ImageWithFocalPoint
          image={data.image}
          sectionId={data.image.id}
          className="rounded-28 object-cover"
          sizes={{
            default: `${BREAKPOINTS.md}px`,
          }}
        />
      )}
    </MobileWrapper>
  );
};

const MediaDesktop = ({ data }: Props) => {
  const isVideo = data?.video?.__typename === 'Video' || data?.video?.__typename === 'VideoJs';

  if (!data.image && !isVideo) {
    return;
  }
  const DesktopWrapper = ({ data, children }: PropsWithChildren<Props>) => (
    <div
      className={`relative col-span-6 transition-all max-[767px]:hidden md:grid md:aspect-square ${
        data.imagePosition !== 'right' ? 'mr-48' : 'ml-48'
      } ${clsx({
        'col-start-7': data.imagePosition === 'right',
      })}`}
    >
      {children}
    </div>
  );

  const DesktopVideoJSWrapper = ({ data, children }: PropsWithChildren<Props>) => (
    <div
      className={`relative col-span-6 transition-all max-[767px]:hidden md:grid md:aspect-square ${
        data.imagePosition !== 'right' ? 'mr-48' : 'ml-48'
      } ${clsx({
        'col-start-7': data.imagePosition === 'right',
      })}`}
    >
      {children}
    </div>
  );

  if (data?.video?.__typename === 'VideoJs') {
    return (
      <DesktopVideoJSWrapper data={data}>
        <VideoJS data={data} />
      </DesktopVideoJSWrapper>
    );
  }

  if (data?.video?.__typename === 'Video') {
    return (
      <DesktopWrapper data={data}>
        <Video data={data} />
      </DesktopWrapper>
    );
  }

  return (
    <DesktopWrapper data={data}>
      {data.image && !isVideo && (
        <ImageWithFocalPoint
          image={data.image}
          sectionId={data.image.id}
          className="rounded-28"
          width={500}
          height={500}
        />
      )}
    </DesktopWrapper>
  );
};

export { MediaMobile };
export default MediaDesktop;
